import {onMounted, onBeforeUnmount} from "vue";
import {createError} from 'nuxt/app';

/**
 * Shared loader for stats pages (club, league cycle, ladder, tournament).
 * Fetches the entity's page data plus its realtimeStats rows; all leaderboard
 * derivation (period/all-time form, metric sorting) lives in StatsView.vue.
 * @param {string} entityType - 'club' | 'leagueCycle' | 'ladder' | 'tournament'
 * @param {string} entityId - The ID of the entity
 * @param {string} pageApiPath - API path to fetch page data (e.g. 'api/v2/page/club')
 * @param {object} statsFilter - Filter for the stats API (e.g. {clubIds: [id]})
 * @param {string} listenerEvent - Event name for real-time updates (optional)
 * @returns {object} page, stats and their fetch/error state
 */
export const useStatsPage = async (entityType, entityId, pageApiPath, statsFilter, listenerEvent = null) => {
  // Initialise both APIs before any await so composables run within the Nuxt instance context
  const {item: page, getItem: getPage, hasItemErrors: hasPageErrors} = useCrudApi(pageApiPath);
  const {items: stats, getItems: getStats, hasItemErrors: hasStatsErrors} = useCrudApi("api/v2/realtimeStats");

  await getPage(entityId);

  if (hasPageErrors.value) {
    throw createError({
      statusCode: 404,
      message: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} not found`
    });
  }

  await getStats(1, 1000, statsFilter);

  // Real-time listeners (if event name provided)
  if (listenerEvent) {
    const {addListener, removeLocalListenersWithEvent} = useListenerService();
    onMounted(() => {
      addListener(listenerEvent, () => getPage(entityId));
    });
    onBeforeUnmount(() => {
      removeLocalListenersWithEvent(listenerEvent);
    });
  }

  return {
    page,
    stats,
    getPage,
    hasPageErrors,
    hasStatsErrors,
  };
};
